import { Icons } from 'src/shared/components/ui/icons/dynamic-icon';
import { STANDARDMODELS, TOPICMODEL } from 'src/shared/constants/models.constants';
import { Condition, ConditionGroup, Flow, FlowBlock } from 'src/features/flows/types/flow.types';
import { Model, Tag, TagNode } from 'src/shared/types/model.types';

/**
 * Check if a model has unpublished changes by looking for tags with pending states
 */
export function getModelHasUnpublishedChanges(model: Model): boolean {
  return (
    model.tags?.some((tag) => ['PENDING_ADDED', 'PENDING_REMOVED', 'PENDING_UPDATED'].includes(tag.status)) || false
  );
}

/**
 * Get pending tags grouped by their status for display in the training tooltip
 */
export function getPendingTagsByStatus(tags: Tag[]) {
  return (tags || []).reduce(
    (acc: { pendingAddition: Tag[]; pendingRemoval: Tag[]; pendingUpdate: Tag[] }, tag: Tag) => {
      switch (tag.status) {
        case 'PENDING_ADDED':
          acc.pendingAddition.push(tag);
          break;
        case 'PENDING_REMOVED':
          acc.pendingRemoval.push(tag);
          break;
        case 'PENDING_UPDATED':
          acc.pendingUpdate.push(tag);
          break;
      }
      return acc;
    },
    {
      pendingAddition: [],
      pendingRemoval: [],
      pendingUpdate: [],
    }
  );
}

export function sortModels(models: Model[]) {
  if (!models?.length) {
    return [];
  }

  const topic = models.filter((model) => model.id.includes(TOPICMODEL)).sort((a, b) => a.id.length - b.id.length);

  const other = models
    .filter(
      (model) =>
        !model.id.includes(TOPICMODEL) && !STANDARDMODELS.includes(model.id) && model.settings?.modelType !== 'ner'
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const entityModels = models
    .filter(
      (model) =>
        !model.id.includes(TOPICMODEL) && !STANDARDMODELS.includes(model.id) && model.settings?.modelType === 'ner'
    )
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  const languageAndSentiment = models
    .filter((model) => STANDARDMODELS.includes(model.id))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));

  return [...topic, ...other, ...entityModels, ...languageAndSentiment].filter(Boolean);
}

export function sortMessageModels(models: [string, string[]][]): [string, string[]][] {
  if (!models?.length) {
    return [];
  }

  const topic = models.filter(([modelId]) => modelId.includes(TOPICMODEL)).sort((a, b) => a.length - b.length);

  const other = models
    .filter(([modelId]) => !modelId.includes(TOPICMODEL) && !STANDARDMODELS.includes(modelId))
    .sort((a, b) => a[0].localeCompare(b[0]));

  const languageAndSentiment = models
    .filter(([modelId]) => STANDARDMODELS.includes(modelId))
    .sort((a, b) => a[0].localeCompare(b[0]));

  return [...topic, ...other, ...languageAndSentiment].filter(Boolean);
}

// returns a list of all the nested children's internalIds of a tag
export function getChildrenIds(tagId: string, model: Model, current: string[] = []) {
  const tag = model.tags.find((tag) => tag.id === tagId);

  if (!tag) {
    return current;
  }

  current = [...current, tag.internalId];

  for (const childTagId of tag.children || []) {
    current = getChildrenIds(childTagId.id, model, current);
  }

  return current;
}

export function getTagHierarchy(tagId: string, tags: Tag[]): string[] {
  const tagsMap: { [key: string]: Tag } = tags.reduce(
    (map, tag) => {
      map[tag.id] = tag;
      return map;
    },
    {} as { [key: string]: Tag }
  );

  function buildHierarchy(tagId: string, currentHierarchy: string[] = []): string[] {
    const tag = tagsMap[tagId];
    if (!tag) {
      return currentHierarchy;
    }
    currentHierarchy.unshift(tag.shortName || tag.internalId);
    if (tag.parentId) {
      return buildHierarchy(tag.parentId, currentHierarchy);
    }
    return currentHierarchy;
  }

  return buildHierarchy(tagId);
}

export function getTagNerHierarchy(tagId: string, tags: Tag[]): string[] {
  const tagsMap: { [key: string]: Tag } = tags.reduce(
    (map, tag) => {
      map[tag.id] = tag;
      return map;
    },
    {} as { [key: string]: Tag }
  );

  function buildHierarchy(tagId: string, currentHierarchy: string[] = []): string[] {
    const tag = tagsMap[tagId];
    if (!tag) {
      return currentHierarchy;
    }
    if (tag.isGrouped) {
      currentHierarchy.unshift('0');
    }
    currentHierarchy.unshift(tag.internalId);
    if (tag.parentId) {
      return buildHierarchy(tag.parentId, currentHierarchy);
    }
    return currentHierarchy;
  }

  return buildHierarchy(tagId);
}

type GetIconForModelProps = { id: string; isNer?: boolean; isProcessModel?: boolean };

export function getIconForModel({ id, isNer, isProcessModel }: GetIconForModelProps): Icons {
  if (isProcessModel) {
    return 'GitCommitVertical';
  }
  if (isNer) {
    return 'ScanSearch';
  }
  if (id === 'sentiment') {
    return 'HeartHandshake';
  }
  if (id === 'language') {
    return 'Globe';
  }
  if (TOPICMODEL.includes(id)) {
    return 'BookType';
  }
  return 'Tag';
}

export function getSentimentColor(sentiment: string) {
  if (sentiment === 'positive') {
    return 'text-success';
  }
  if (sentiment === 'negative') {
    return 'text-destructive';
  }
  return 'text-neutral-600';
}

export function getBadgeIconColor(modelId: string, tagValue: string) {
  if (modelId === 'sentiment') {
    switch (tagValue) {
      case 'stroke-success':
        return 'success';
      case 'negative':
        return 'stroke-destructive';
      default:
        return;
    }
  }
  if (modelId !== 'language') {
    return 'stroke-dashboardblue';
  }
}

function escapeCSVField(field: string): string {
  // If field contains quotes, escape them by doubling them
  // Then wrap the entire field in quotes if it contains commas, quotes, or newlines
  const escaped = field.replace(/"/g, '""');
  if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
    return `"${escaped}"`;
  }
  return field;
}

export function exportTagsToCsv(modelName: string, tags: Tag[]) {
  const csvContent = 'data:text/csv;charset=utf-8,';
  const header = 'parent_id,id,display_name,description\n'; // keep these the same as the bulk upload format
  const rows = tags
    .map((tag) => {
      const parentInternalId = tag.parentId ? tags.find((t) => t.id === tag.parentId)?.internalId || '' : '';
      return `${escapeCSVField(parentInternalId)},${escapeCSVField(tag.internalId)},${escapeCSVField(tag.shortName)},${escapeCSVField(tag.description)}`;
    })
    .join('\n');
  const csv = csvContent + header + rows;
  const encodedUri = encodeURI(csv);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `tekst-${modelName}-tags.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function exportFeedbackToCsv(modelName: string, tags: Tag[]) {
  const csvContent = 'data:text/csv;charset=utf-8,';
  const header = 'id,feedback\n';
  const rows = tags
    .map((tag) => {
      const feedback = tag.feedback.map((f) => JSON.stringify(f.conditions));
      return `${escapeCSVField(tag.internalId)},${escapeCSVField(feedback.join(';'))}`;
    })
    .join('\n');
  const csv = csvContent + header + rows;
  const encodedUri = encodeURI(csv);
  const link = document.createElement('a');
  link.setAttribute('href', encodedUri);
  link.setAttribute('download', `tekst-${modelName}-feedback.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export function getFlowsForTag(modelId: string, tagInternalId: string, flows: Flow[]) {
  if (!flows?.length) {
    return [];
  }

  return flows
    .filter((flow) => {
      if (!flow.isRunning) {
        return false;
      }
      const conditionBlocks = flow.stable.filter((block) => block.type === 'condition');
      if (!conditionBlocks?.length) {
        return false;
      }
      return conditionBlocks.some((conditionBlock: FlowBlock) =>
        findTagInCondition(conditionBlock.blockContent.conditionGroup, tagInternalId, modelId)
      );
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

function findTagInCondition(condition: Condition | ConditionGroup, tagInternalId: string, modelId: string): boolean {
  if (condition.isGroup === true) {
    return condition.conditions.some((condition) => findTagInCondition(condition, tagInternalId, modelId));
  } else {
    if (typeof condition.rightOperand === 'string') {
      return condition.leftOperand.includes(modelId) && condition.rightOperand === tagInternalId;
    } else if (Array.isArray(condition.rightOperand)) {
      return condition.leftOperand.includes(modelId) && condition.rightOperand.includes(tagInternalId);
    }
    return false;
  }
}

export type ModelMap = Map<
  string,
  Omit<Model, 'tags'> & {
    tags: Map<string, Tag>;
  }
>;

export function getModelMap(models: Model[]): ModelMap {
  if (!models?.length) {
    return new Map();
  }
  return new Map(
    models?.map((model) => {
      const tagMap = new Map();
      for (const tag of model.tags) {
        tagMap.set(tag.id, tag); // both the id and the internalId are used as keys in the tagMap
        tagMap.set(tag.internalId, tag);
      }
      return [model.id, { ...model, tags: tagMap }];
    })
  );
}

export function isValidTagTree(tree: TagNode[]): boolean {
  const allInternalIds = new Set<string>();

  function validateTree(nodes: TagNode[]): boolean {
    return nodes.every((tag) => {
      // Skip validation if all fields are empty
      if (tag.internalId.length === 0 && tag.shortName.length === 0 && tag.description.length === 0) {
        return true;
      }

      if (tag.internalId.length === 0 || tag.shortName.length === 0) {
        return false;
      }

      if (allInternalIds.has(tag.internalId)) {
        return false;
      }

      allInternalIds.add(tag.internalId);

      return !tag.children || validateTree(tag.children);
    });
  }

  return tree.length > 0 && validateTree(tree);
}
